import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import EntityFormFields from '../../../shared/EntityFormFields';
import FormSection from '../../../shared/FormSection';
import DeploymentLifecycleFields, {
  emptyDeploymentStatusDates,
  emptyDeploymentStatusApprovals,
} from '../../../shared/DeploymentLifecycleFields';
import {
  CONTRACT_TERM_FIELDS,
  LICENSE_TERM_FIELDS,
  IDENTIFICATION_FIELDS,
  HOSTING_FIELDS,
  LOOKUP_CONFIGS,
} from '../../../config/wizardSteps';
import { softwareApi } from '../../../api/systemRegisterApi';
import { unwrapData } from '../../../utils/responseHelpers';
import { applyTermFieldChange } from '../../../utils/termDateHelpers';
import WizardLookupStep from './WizardLookupStep';

/** Identification step control size: 280px on desktop, full width on mobile, ~40px tall (MUI small). */
const ID_FIELD_WIDTH = 280;

const toCompactFields = (fields) =>
  fields.map((field) => ({
    ...field,
    inputSize: 'small',
    labelAbove: field.type === 'enum' ? false : field.labelAbove,
    sx: {
      ...(field.sx || {}),
      width: '100%',
      '& .MuiInputBase-root': {
        height: 40,
        minHeight: 40,
      },
    },
    selectSx: {
      minWidth: '100%',
      width: '100%',
      height: 40,
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        minHeight: '1.4375em',
        boxSizing: 'border-box',
        py: 0.75,
      },
    },
  }));

const WizardStepSystemIdentification = ({
  values,
  errors,
  touched,
  onChange,
  onSetField,
  onLookupChange,
  lookups,
  onRefreshLookups,
  systemName,
}) => {
  useEffect(() => {
    if (values.internalReference) return undefined;
    let cancelled = false;
    softwareApi
      .nextInternalReference()
      .then((res) => {
        const next = unwrapData(res);
        if (!cancelled && next) onSetField('internalReference', String(next));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Preview next ID once when creating a new registration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identificationFields = useMemo(() => toCompactFields(IDENTIFICATION_FIELDS), []);
  const contractFields = useMemo(() => toCompactFields(CONTRACT_TERM_FIELDS), []);
  const licenseFields = useMemo(() => toCompactFields(LICENSE_TERM_FIELDS), []);
  const hostingFields = useMemo(() => toCompactFields(HOSTING_FIELDS), []);

  const fieldProps = {
    values,
    errors,
    touched,
    handleChange: (e) => onChange(e.target.name, e.target.value),
    handleBlur: () => {},
    setFieldValue: onSetField,
    columns: 2,
    dense: true,
    fixedFieldWidth: ID_FIELD_WIDTH,
  };

  const handleTermField = (prefix) => (name, value) => {
    applyTermFieldChange(values, prefix, name, value, onSetField);
  };

  const termFieldProps = (prefix) => {
    const onField = handleTermField(prefix);
    return {
      ...fieldProps,
      handleChange: (e) => onField(e.target.name, e.target.value),
      setFieldValue: onField,
    };
  };

  const handleStatusChange = (status) => {
    onSetField('deploymentStatus', status);
  };

  const handleDateChange = (key, value) => {
    const next = {
      ...(values.deploymentStatusDates || emptyDeploymentStatusDates()),
      [key]: value,
    };
    onSetField('deploymentStatusDates', next);
    if (key === 'production') {
      onSetField('firstDeployedDate', value || '');
    }
  };

  const handleApprovalsChange = (next) => {
    onSetField('deploymentStatusApprovals', next || emptyDeploymentStatusApprovals());
  };

  const lookupStep = (key, stateKey) => ({
    id: key,
    label: LOOKUP_CONFIGS[key].label,
    lookupKey: LOOKUP_CONFIGS[key].lookupKey,
    optionLabel: LOOKUP_CONFIGS[key].optionLabel,
    stateKey,
  });

  const compactFieldSx = {
    width: { xs: '100%', sm: ID_FIELD_WIDTH },
    flexShrink: 0,
  };

  return (
    <>
      <FormSection title="System identity">
        <EntityFormFields fields={identificationFields} {...fieldProps} />
      </FormSection>

      <FormSection title="Contract">
        <EntityFormFields fields={contractFields} {...termFieldProps('contract')} />
      </FormSection>

      <FormSection title="License">
        <EntityFormFields fields={licenseFields} {...termFieldProps('license')} />
      </FormSection>

      <FormSection title="Deployment status & timeline">
        <DeploymentLifecycleFields
          deploymentStatus={values.deploymentStatus}
          deploymentEnvironment={values.deploymentEnvironment}
          deploymentStatusDates={values.deploymentStatusDates || emptyDeploymentStatusDates()}
          deploymentStatusApprovals={
            values.deploymentStatusApprovals || emptyDeploymentStatusApprovals()
          }
          onStatusChange={handleStatusChange}
          onEnvironmentChange={(env) => onSetField('deploymentEnvironment', env)}
          onDateChange={handleDateChange}
          onApprovalsChange={handleApprovalsChange}
          error={errors?.deploymentStatus}
          dateError={errors?.deploymentStatusDates}
          approvalUiMode="none"
          compactControls
          controlWidth={ID_FIELD_WIDTH}
          afterEnvironment={<EntityFormFields fields={hostingFields} {...fieldProps} />}
        />
      </FormSection>

      <FormSection title="Ownership & provider">
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box sx={compactFieldSx}>
            <WizardLookupStep
              step={lookupStep('businessUnits', 'businessUnitIds')}
              selectedIds={values.businessUnitIds}
              onChange={(ids) => onLookupChange('businessUnitIds', ids)}
              lookups={lookups}
              onRefreshLookups={onRefreshLookups}
              assignmentTargetName={systemName}
              errors={errors}
            />
          </Box>
          <Box sx={compactFieldSx}>
            <WizardLookupStep
              step={lookupStep('systemOwners', 'systemOwnerIds')}
              selectedIds={values.systemOwnerIds}
              onChange={(ids) => onLookupChange('systemOwnerIds', ids)}
              lookups={lookups}
              onRefreshLookups={onRefreshLookups}
              assignmentTargetName={systemName}
              errors={errors}
            />
          </Box>
          <Box sx={compactFieldSx}>
            <WizardLookupStep
              step={lookupStep('vendors', 'vendorIds')}
              selectedIds={values.vendorIds}
              onChange={(ids) => onLookupChange('vendorIds', ids)}
              lookups={lookups}
              onRefreshLookups={onRefreshLookups}
              assignmentTargetName={systemName}
              errors={errors}
            />
          </Box>
        </Box>
      </FormSection>
    </>
  );
};

export default WizardStepSystemIdentification;
